import React, { useState } from "react";
import { FaDownload } from "react-icons/fa6";
import {
  Accordion,
  Box,
  Center,
  HStack,
  Icon,
  Spacer,
  Spinner,
  Stack,
  Table,
} from "@chakra-ui/react";
import { Text } from "@/components";
import { useIsMobile } from "@/hooks";
import { UploadedDocument } from "@/types";

type DocumentsTableProps = {
  documents: UploadedDocument[]
  handleDownloadDocument: (name: string) => void
};

const DownloadDocumentButton = ({ name, handleDownloadDocument }: { name: string, handleDownloadDocument: (name: string) => void }) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  
  const onDownloadDocument = async () => {
    setIsLoading(true);
    await handleDownloadDocument(name);
    setIsLoading(false);
  }

  return (
    <Center
      border="1px solid #E2E8F0"
      borderRadius={5}
      boxSize={8}
      cursor="pointer"
      _hover={{ bg: "gray.200" }}
      onClick={onDownloadDocument}
    >
      {
        isLoading ? (
          <Spinner size="sm" />
        ) : <Icon as={FaDownload as React.ElementType} color="black" />
      }
    </Center>
  )
}

export const DocumentsTable: React.FC<DocumentsTableProps> = ({ documents, handleDownloadDocument }) => {
  const isMobile = useIsMobile()
  const BUCKET_NAMES: Record<string, string> = {
    nda: "NDA and Background Check",
    bc: "NDA and Background Check",
    grant: "Grant Documents",
    identification: "Identification",
    contract: "Contract",
    deal: "Deal Documents",
    compliance: "Compliance",
    other: "Other",
  };

  const groupedDocs = documents.reduce<Record<string, UploadedDocument[]>>((acc, doc) => {
    const rawType = (doc.type || "other").toLowerCase();
    const type = BUCKET_NAMES[rawType] ?? doc.type ?? "Other";
    if (!acc[type]) acc[type] = [];
    acc[type].push(doc);
    return acc;
  }, {});

  if (Object.keys(groupedDocs).length === 0) return null;

  return (
    <Stack w="100%" pt={10} pb={10} gap={4}>
      <HStack justifyContent="space-between">
        <Text fontSize="md" fontWeight="bold">Already uploaded</Text>
        <Text fontSize="sm" color="gray.500">From earlier stages</Text>
      </HStack>
      <Accordion.Root multiple defaultValue={Object.keys(groupedDocs)}>
        <Stack gap={3}>
          {Object.entries(groupedDocs).map(([type, docs]) => (
            <Box
              key={type}
              borderWidth="1px"
              borderColor="gray.200"
              borderRadius="lg"
              overflow="hidden"
            >
              <Accordion.Item value={type}>
                <Accordion.ItemTrigger cursor="pointer" px={5} py={3} bg="gray.50" _hover={{ bg: "gray.100" }}>
                  <Accordion.ItemIndicator />
                  <Text fontWeight="bold" fontSize="md">{type}</Text>
                  <Spacer />
                  <Text fontSize="sm" color="gray.500" fontWeight="medium">{docs.length}</Text>
                </Accordion.ItemTrigger>
                <Accordion.ItemContent>
                  <Accordion.ItemBody px={0} pb={0}>
                    <Box overflowX="auto"><Table.Root>
                      <Table.Header>
                        <Table.Row>
                          <Table.ColumnHeader><Text fontWeight="bold">Document Name</Text></Table.ColumnHeader>
                          <Table.ColumnHeader><Text fontWeight="bold">Date</Text></Table.ColumnHeader>
                          <Table.ColumnHeader><Text fontWeight="bold">Uploaded By</Text></Table.ColumnHeader>
                          <Table.ColumnHeader textAlign="center"></Table.ColumnHeader>
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        {docs.map((doc) => (
                          <Table.Row key={doc.name}>
                            <Table.Cell>
                              <Text fontSize="sm" fontWeight="medium">{doc.name}</Text>
                            </Table.Cell>
                            <Table.Cell>
                              <Text fontSize="sm" fontWeight="medium">
                                {new Intl.DateTimeFormat("en-US", isMobile
                                  ? { month: "2-digit", year: "2-digit" }
                                  : { month: "2-digit", day: "2-digit", year: "numeric" }
                                ).format(new Date(doc.date))}
                              </Text>
                            </Table.Cell>
                            <Table.Cell>
                              <Text fontSize="sm" fontWeight="medium">{doc.uploadedBy}</Text>
                            </Table.Cell>
                            <Table.Cell textAlign="center">
                              <DownloadDocumentButton name={doc.name} handleDownloadDocument={handleDownloadDocument} />
                            </Table.Cell>
                          </Table.Row>
                        ))}
                      </Table.Body>
                    </Table.Root></Box>
                  </Accordion.ItemBody>
                </Accordion.ItemContent>
              </Accordion.Item>
            </Box>
          ))}
        </Stack>
      </Accordion.Root>
    </Stack>
  )
}